import httpClient from "@/api/config/httpClient";
import {
	CustomerAccountSummaryDto,
	SearchCustomerFilters,
	CustomerFollowUpFilter,
	TerminateProductOrderData,
	CustomerProductStateType,
	CustomerAggrupation,
	CustomerWorkTaskDto,
	CustomerBalancesAndDebtDto,
	CustomerAvailableChargesAndCreditsDto,
	CreateCustomerChargeOrCreditDto,
	CustomerGrupoAireWorkTaskDto,
	CustomerJiraTicketDto,
	CustomerJiraTicketsFiltersDto,
	CustomerDocumentDto,
	CreateCustomerDocumentDto,
	UpdateCustomerDocumentDto
} from "@/api/models/customer";
import { ModificationCheck } from "@/api/models/product-modification";
import { TablePaginationFilter } from "@/models/table.model";
import { AxiosResponse } from "axios";
import { MultipleTransitionBundlesData } from "../models/product-multiple-transition/productMultipleTransition.model";
import { ProductTransitionGroup } from "../models/product-transition/productTransitionGroup.model";
import { transitionCheckToTransitionCheckAPI } from "../adapters/product-transition/productTransition.adapter";
import { TransitionCheck } from "../models/product-transition/productTransition.model";
import { modificationCheckToModificationCheckAPI } from "../adapters/product-modification/productModification.adapter";
import { HttpUtil } from "../config/httpUtil";
import { DropdownOptionDto, GenericSearchResDto } from "../models/common.model";
import {
	CustomerBillingAccountDto,
	UpdateCustomerBillingAdditionalDetailsDto,
	UpdateCustomerBillingBillReceiverDeliveryContactDto,
	UpdateCustomerBillingInvoiceDetailsDto,
	UpdateCustomerIdentificationAndCustomerBillingPaymentMethodDto
} from "../models/customer/customerBilling.model";

export abstract class CustomerService {
	private static readonly CUSTOMER_URL = "/sys03/customer";
	private static readonly CUSTOMER_BILLING_URL = "/sys03/customer/billing";
	private static readonly DASHBOARD_URL = CustomerService.CUSTOMER_URL.concat("/dashboard");
	private static readonly DASHBOARD_CSV_URL = CustomerService.DASHBOARD_URL.concat("/csv");
	private static readonly HISTORY_URL = CustomerService.CUSTOMER_URL.concat("/by-user/history");

	public static getById({ id }: { id: string }): Promise<AxiosResponse<CustomerAccountSummaryDto>> {
		const url = `${CustomerService.CUSTOMER_URL}/${id}`;

		return httpClient.get(url);
	}

	public static getBilling({ id }: { id: string }): Promise<AxiosResponse<CustomerBillingAccountDto>> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}`;

		return httpClient.get(url);
	}

	public static getBillingCyclesOptions({ id }: { id: string }): Promise<AxiosResponse<DropdownOptionDto[]>> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/cycles/options`;

		return httpClient.get(url);
	}

	public static getDeliveryMethodsOptions({ id }: { id: string }): Promise<AxiosResponse<DropdownOptionDto[]>> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/delivery-methods/options`;

		return httpClient.get(url);
	}

	public static updateCustomerIdentificationAndCustomerBillingPaymentMethod({
		id,
		identificationAndPaymentMethod
	}: {
		id: string;
		identificationAndPaymentMethod: UpdateCustomerIdentificationAndCustomerBillingPaymentMethodDto;
	}): Promise<void> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/identification-and-payment-method`;

		return httpClient.put(url, identificationAndPaymentMethod);
	}

	public static updateCustomerBillingBillReceiverDeliveryContact({
		id,
		deliveryContact
	}: {
		id: string;
		deliveryContact: UpdateCustomerBillingBillReceiverDeliveryContactDto;
	}): Promise<void> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/bill-receiver-delivery-contact`;

		return httpClient.put(url, deliveryContact);
	}

	public static updateCustomerBillingInvoiceDetails({
		id,
		invoiceDetails
	}: {
		id: string;
		invoiceDetails: UpdateCustomerBillingInvoiceDetailsDto;
	}): Promise<void> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/invoice-details`;

		return httpClient.put(url, invoiceDetails);
	}

	public static updateCustomerBillingInvoiceDetailsRevertBillingCycle({ id }: { id: string }): Promise<void> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/invoice-details/revert-billing-cycle`;

		return httpClient.put(url);
	}

	public static updateCustomerBillingAdditionalDetails({
		id,
		additionalDetails
	}: {
		id: string;
		additionalDetails: UpdateCustomerBillingAdditionalDetailsDto;
	}): Promise<void> {
		const url = `${CustomerService.CUSTOMER_BILLING_URL}/${id}/additional-details`;

		return httpClient.put(url, additionalDetails);
	}

	public static getBalancesAndDebt({ id }: { id: string }): Promise<AxiosResponse<CustomerBalancesAndDebtDto>> {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/balances-and-debt`;

		return httpClient.get(url);
	}

	public static getDashboardInformation(filters: SearchCustomerFilters) {
		const url = `${CustomerService.DASHBOARD_URL}`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getHistoryInformation() {
		const url = `${CustomerService.HISTORY_URL}`;
		return httpClient.get(url);
	}

	public static getDashboardTransitionsById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/transitions`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getDashboardMultipleTransitionsById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/multiple-transitions`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getDashboardModificationsById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/modifications`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerDashboardNewExtrasById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/new-extras`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerDashboardTerminateById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/terminate`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerDashboardChangeOfAddressesById({ customerId, filters }: { customerId: string; filters: CustomerFollowUpFilter }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/dashboard/change-of-addresses`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getDashboardCsv(filters: SearchCustomerFilters) {
		const url = `${CustomerService.DASHBOARD_CSV_URL}`;
		const filtersWithoutPagination: SearchCustomerFilters = {
			...filters,
			page: undefined,
			limit: undefined
		};
		const params = HttpUtil.getFiltersParameters({ filters: filtersWithoutPagination });

		return httpClient.get(url, { params });
	}

	public static getCustomerFinancesById(customerId: string, filters: TablePaginationFilter) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/finance`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static createCustomerDocument({ id, document }: { id: string; document: CreateCustomerDocumentDto }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/documents`;

		const { fileName, file, type, comment } = document;

		const formData = new FormData();
		formData.append("fileName", fileName);
		formData.append("file", file);
		formData.append("type", type);
		if (comment) {
			formData.append("comment", comment);
		}

		return httpClient.post(url, formData, {
			headers: {
				"Content-Type": "multipart/form-data"
			}
		});
	}

	public static getCustomerDocuments({ id }: { id: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/documents`;
		return httpClient.get<GenericSearchResDto<CustomerDocumentDto>>(url);
	}

	public static downloadCustomerDocument({ id, documentId }: { id: string; documentId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/documents/${documentId}/download`;

		return httpClient.get(url, { responseType: "blob" });
	}

	public static updateCustomerDocument({ id, documentId, document }: { id: string; documentId: string; document: UpdateCustomerDocumentDto }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/documents/${documentId}`;

		return httpClient.put(url, document);
	}

	public static deleteCustomerDocument({ id, documentId }: { id: string; documentId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/documents/${documentId}`;

		return httpClient.delete(url);
	}

	public static getCustomerNotesById(id: string, filters: TablePaginationFilter) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/notes`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerTasksById(id: string, filters: TablePaginationFilter) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/tasks`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerTaskById(customerId: string, taskId: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/tasks/${taskId}`;
		return httpClient.get(url);
	}

	public static getCustomerOrdersById(id: string, filters: TablePaginationFilter) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/orders`;
		const params = HttpUtil.getFiltersParameters({ filters });

		return httpClient.get(url, { params });
	}

	public static getCustomerWorkTasks(id: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/work-tasks`;

		return httpClient.get<GenericSearchResDto<CustomerWorkTaskDto>>(url);
	}

	public static getCustomerGrupoAireWorkTasks(id: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/grupo-aire/work-tasks`;

		return httpClient.get<GenericSearchResDto<CustomerGrupoAireWorkTaskDto>>(url);
	}

	public static getCustomerJiraTickets(id: string, filters: CustomerJiraTicketsFiltersDto) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/jira/tickets`;

		return httpClient.get<GenericSearchResDto<CustomerJiraTicketDto>>(url, { params: filters });
	}

	public static getCustomerOrderById(customerId: string, orderId: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/orders/${orderId}`;
		return httpClient.get(url);
	}

	public static getCustomerChargesAndCreditsById(id: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/charges-and-credits`;
		return httpClient.get(url);
	}

	public static getCustomerAvailableChargesAndCredits() {
		const url = `${CustomerService.CUSTOMER_URL}/available-charges-and-credits`;
		return httpClient.get<CustomerAvailableChargesAndCreditsDto>(url);
	}

	public static createCustomerChargeOrCredit({ id, chargeOrCredit }: { id: string; chargeOrCredit: CreateCustomerChargeOrCreditDto }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/charge-or-credit`;
		return httpClient.post(url, chargeOrCredit);
	}

	public static getCustomerAggrupationValidateById(aggrupation: CustomerAggrupation) {
		const { id: aggrupationId } = aggrupation;
		const url = `${CustomerService.CUSTOMER_URL}/aggrupations/${aggrupationId}/check`;
		return httpClient.get(url);
	}

	public static getCustomerAggrupationValidate(customerId: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/aggrupations/check`;
		return httpClient.get(url);
	}

	public static createCustomerAggrupation(aggrupation: CustomerAggrupation) {
		const aggrupationsUrl = `${CustomerService.CUSTOMER_URL}/products/merge`;
		return httpClient.post(aggrupationsUrl, aggrupation);
	}

	public static unMergeCustomerAggrupation(aggrupation: CustomerAggrupation) {
		const { customerId, id: aggrupationId } = aggrupation;
		const aggrupationsUrl = `${CustomerService.CUSTOMER_URL}/${customerId}/aggrupations/${aggrupationId}/unmerge`;
		return httpClient.put(aggrupationsUrl, aggrupation);
	}

	public static sendCustomerAggrupation(aggrupation: CustomerAggrupation) {
		const { id: aggrupationId, customerId } = aggrupation;
		const aggrupationsUrl = `${CustomerService.CUSTOMER_URL}/${customerId}/aggrupations/${aggrupationId}/digitel/send`;
		return httpClient.put(aggrupationsUrl);
	}

	public static getCustomerProductsForAggrupation({ customerId }: { customerId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${customerId}/products-ready`;
		return httpClient.get<GenericSearchResDto<any>>(url).then((response) => response.data);
	}

	public static getCustomerProductsById(id: string, states?: CustomerProductStateType[]) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/products`;
		if (states && states.length > 0) {
			const params = new URLSearchParams();
			for (const state of states) {
				params.append("state", state);
			}

			return httpClient.get(url, {
				params
			});
		}

		return httpClient.get(url);
	}

	public static getCustomerProductById(id: string) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}`;
		return httpClient.get(url);
	}

	public static getCustomerProductTransitionGroups({ id, action, productId }: { id: string; action: string; productId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/product-transition-groups`;
		return httpClient.get<ProductTransitionGroup[]>(url, {
			params: { action, productId }
		});
	}

	/**
	 * Validate method to customer products actions
	 */
	public static getCustomerProductsActionValidateById(id: string, action: string) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/action/${action}/validate`;
		return httpClient.get(url);
	}

	public static getCustomerProductsActionDuplicateValidateById(id: string, action: string) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/action/${action}/validate/duplicate`;
		return httpClient.get(url);
	}

	/**
	 * Validate method to follow up actions
	 */
	public static getCustomerProductsActionOrderValidateById(id: string, action: string) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/action/${action}/validate/order`;
		return httpClient.get(url);
	}

	public static checkCustomerProductTransitionById(productId: string, transition: TransitionCheck) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${productId}/transition/check`;
		return httpClient.put<{ shoppingCartId: string }>(url, transitionCheckToTransitionCheckAPI(transition));
	}

	public static checkCustomerProductOnlyMobilesTransitionById(productId: string, transition: TransitionCheck) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${productId}/transition/only-mobiles/check`;
		return httpClient.put<{ shoppingCartId: string }>(url, transitionCheckToTransitionCheckAPI(transition));
	}

	public static checkCustomerProductSharedMobilesTransitionById(productId: string, transition: TransitionCheck) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${productId}/transition/shared-mobiles/check`;
		return httpClient.put<{ shoppingCartId: string }>(url, transitionCheckToTransitionCheckAPI(transition));
	}

	public static getCustomerProductTransitionById({ id, shoppingCartId }: { id: string; shoppingCartId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/transition`;
		return httpClient.get(url, {
			params: {
				shoppingCartId
			}
		});
	}

	public static getCustomerProductModificationById({ id, shoppingCartId }: { id: string; shoppingCartId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/modification`;
		return httpClient.get(url, {
			params: {
				shoppingCartId
			}
		});
	}

	public static checkCustomerProductModificationById({ productId, modification }: { productId: string; modification: ModificationCheck }) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${productId}/modification/check`;
		return httpClient.put<{ shoppingCartId: string }>(url, modificationCheckToModificationCheckAPI(modification));
	}

	public static terminateCustomerProductById({ id, order }: { id: string; order: TerminateProductOrderData }) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/terminate`;
		return httpClient.put(url, order);
	}

	public static deleteCustomerProductTerminate(terminateId: string) {
		const url = `${CustomerService.CUSTOMER_URL}/product/deactivation-process/${terminateId}`;
		return httpClient.delete(url);
	}

	public static getCustomerAvailableExtrasValidateById(id: string) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/available-extras/validate`;
		return httpClient.get(url);
	}

	public static getCustomerAvailableExtras({ id, shoppingCartId }: { id: string; shoppingCartId: string }) {
		const url = `${CustomerService.CUSTOMER_URL}/${id}/available-extras`;
		return httpClient.get(url, {
			params: {
				shoppingCartId
			}
		});
	}

	public static getCustomerProductOnlyMobilesTransitionBundles({
		id,
		shoppingCartId,
		groupId
	}: {
		id: string;
		shoppingCartId: string;
		groupId?: number;
	}) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/transition/only-mobiles`;
		return httpClient.get<MultipleTransitionBundlesData>(url, {
			params: {
				shoppingCartId,
				groupId
			}
		});
	}

	public static getCustomerProductSharedMobilesTransitionBundles({
		id,
		shoppingCartId,
		groupId
	}: {
		id: string;
		shoppingCartId: string;
		groupId?: number;
	}) {
		const url = `${CustomerService.CUSTOMER_URL}/product/${id}/transition/shared-mobiles`;
		return httpClient.get<MultipleTransitionBundlesData>(url, {
			params: {
				shoppingCartId,
				groupId
			}
		});
	}
}
